/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import RenameDiscussionModal from 'flarum/forum/components/RenameDiscussionModal';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;
import Button from 'flarum/common/components/Button';
import type Post from 'flarum/common/models/Post';

// CORE_CODE_OVERRIDE
export default function () {
	override(RenameDiscussionModal.prototype, 'onsubmit', async function (original, e) {
		const review = this.discussion && this.discussion.review();

		if (this.discussion?.isReview() && review && review.shouldSync()) {
			// Copied from core, because the original swallows the error that
			// `Auth.authenticatedCall` needs to see.
			e.preventDefault();

			const saveCallback = async () => {
				// `Modal#loading` is protected and only `loaded()` is public, so the spinner is switched on by name.
				Reflect.set(this, 'loading', true);

				const title = this.newTitle();
				const currentTitle = this.currentTitle;

				if (title && title !== currentTitle) {
					try {
						await this.discussion.save({ title });
						// A `RequestError`, but TypeScript only permits `any` or `unknown` here.
					} catch (error: any) {
						if (error.status !== 401) {
							this.loaded();
						}

						throw error;
					}

					if (app.viewingDiscussion(this.discussion)) {
						app.current.get('stream').update();
					}
					m.redraw();
					this.hide();
				} else {
					this.hide();
				}

				if (review.exportSyncFailed()) {
					app.alerts.show(app.translator.trans('glowingblue-orell-rezensionen.forum.alerts.sync_failed'));
				}
			};

			return await Auth.authenticatedCall(saveCallback);
		}

		return await original(e);
	});

	override(EditPostComposer.prototype, 'onsubmit', async function (original) {
		const discussion = this.attrs.post?.discussion();

		// Only the first post is the review, so only editing that one may demand a linked shop
		// account. A missing id must never pass for the review's — hence the explicit checks.
		const postId = this.attrs.post?.id();
		const firstPostId = discussion?.firstPost()?.id();
		const isReviewPost = postId !== undefined && postId !== null && postId === firstPostId;

		if (discussion?.isReview() && discussion?.review()?.shouldSync() && isReviewPost) {
			const saveCallback = async () => {
				// `Modal#loading` is protected and only `loaded()` is public, so the spinner is switched on by name.
				Reflect.set(this, 'loading', true);

				const data = this.data();

				let post: Post;

				try {
					post = await this.attrs.post.save(data);
					// A `RequestError`, but TypeScript only permits `any` or `unknown` here.
				} catch (error: any) {
					if (error.status !== 401) {
						this.loaded();
					}

					throw error;
				}

				if (app.viewingDiscussion(discussion)) {
					app.current.get('stream').goToNumber(post.number());
				} else {
					// Elsewhere, offer a link to the edited post instead of scrolling to it.
					const alert = app.alerts.show(
						{
							type: 'success',
							controls: [
								<Button
									className="Button Button--link"
									onclick={() => {
										m.route.set(app.route.post(post));
										app.alerts.dismiss(alert);
									}}
								>
									{app.translator.trans('core.forum.composer_edit.view_button')}
								</Button>,
							],
						},
						app.translator.trans('core.forum.composer_edit.edited_message')
					);
				}

				if (this.attrs.post.discussion()?.review()?.exportSyncFailed()) {
					app.alerts.show(app.translator.trans('glowingblue-orell-rezensionen.forum.alerts.sync_failed'));
				}

				this.composer.hide();
			};

			return await Auth.authenticatedCall(saveCallback);
		}

		return original();
	});
}
